import React, { useState, useEffect } from 'react';
import youtubeService from '../services/youtubeService';
import YouTubeSubscriptionForm from '../components/YouTubeSubscriptionForm';
import YouTubeSubscriptionList from '../components/YouTubeSubscriptionList';

const YouTubeManagementPage = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const data = await youtubeService.getAllSubscriptions();
      setSubscriptions(data.subscriptions || []);
      setError('');
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
      setError('Failed to load subscriptions: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setIsEditing(false);
    setSelectedSubscription(null);
    setShowModal(true);
  };

  const handleEdit = (subscription) => {
    setIsEditing(true);
    setSelectedSubscription(subscription);
    setShowModal(true);
  };

  const handleDelete = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to delete this subscription? This will unsubscribe from the channel and stop all notifications.')) {
      return;
    }

    try {
      await youtubeService.deleteSubscription(subscriptionId);
      await fetchSubscriptions();
    } catch (err) {
      console.error('Error deleting subscription:', err);
      alert('Failed to delete subscription: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSave = async (formData) => {
    try {
      if (isEditing && selectedSubscription) {
        await youtubeService.updateSubscription(selectedSubscription.id, formData);
      } else {
        await youtubeService.createSubscription(formData);
      }
      setShowModal(false);
      await fetchSubscriptions();
    } catch (err) {
      console.error('Error saving subscription:', err);
      throw err;
    }
  };

  const handlePause = async (subscriptionId) => {
    if (!window.confirm('Pause this subscription? Notifications will stop until you resume.')) {
      return;
    }

    try {
      await youtubeService.pauseSubscription(subscriptionId);
      await fetchSubscriptions();
    } catch (err) {
      console.error('Error pausing subscription:', err);
      alert('Failed to pause subscription: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleResume = async (subscriptionId) => {
    try {
      await youtubeService.resumeSubscription(subscriptionId);
      await fetchSubscriptions();
    } catch (err) {
      console.error('Error resuming subscription:', err);
      alert('Failed to resume subscription: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleResubscribe = async (subscriptionId) => {
    if (!window.confirm('Resubscribe to this channel? This will renew the subscription lease.')) {
      return;
    }

    try {
      await youtubeService.resubscribe(subscriptionId);
      await fetchSubscriptions();
      alert('Resubscription initiated. YouTube will verify the subscription shortly.');
    } catch (err) {
      console.error('Error resubscribing:', err);
      alert('Failed to resubscribe: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4 align-items-center">
        <div className="col">
          <h2>
            <i className="bi bi-youtube me-2 text-danger"></i>
            YouTube Channel Subscriptions
          </h2>
          <p className="text-muted mb-0">
            Subscribe to YouTube channels and receive MS Teams notifications when new videos are uploaded
          </p>
        </div>
      </div>

      {/* Info card */}
      <div className="row mb-4">
        <div className="col">
          <div className="card bg-light">
            <div className="card-body">
              <h6 className="card-title">
                <i className="bi bi-info-circle me-2"></i>
                How it works
              </h6>
              <ul className="mb-0">
                <li>Subscribe to any YouTube channel by providing its Channel ID</li>
                <li>Configure a MS Teams webhook URL to receive notifications</li>
                <li>When a new video is uploaded, you'll receive a Teams message with the video link</li>
                <li>Subscriptions are renewed automatically before they expire</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Create button */}
      <div className="row mb-3">
        <div className="col">
          <button
            className="btn btn-primary"
            onClick={handleCreate}
            disabled={loading}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Create New Subscription
          </button>
        </div>
      </div>

      {/* Subscriptions list */}
      <div className="row">
        <div className="col">
          <YouTubeSubscriptionList
            subscriptions={subscriptions}
            loading={loading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onResubscribe={handleResubscribe}
            onPause={handlePause}
            onResume={handleResume}
          />
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isEditing ? 'Edit Subscription' : 'Create New Subscription'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <YouTubeSubscriptionForm
                  initialData={selectedSubscription}
                  isEditing={isEditing}
                  onSave={handleSave}
                  onCancel={() => setShowModal(false)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default YouTubeManagementPage;
